import { Box, IconButton, TextField, Tooltip, Typography, styled } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'

import { IBankInstitution } from '@/types'
import { parseDisplayOrder } from './displayOrder'

// What the page tells each display order cell. Only one row is edited at a time, so the page holds
// which one and every other row's edit button is disabled until it is saved or cancelled.
//
// The typed draft lives on the page too, not in the cell: DataTable keys its rows by position, so
// re-sorting or searching remounts the cell and would otherwise drop what was typed.
export interface DisplayOrderEditing {
  canEdit: boolean
  // The row being edited, or null when none is, or when the one being edited is not on screen.
  editingId: string | null
  draft: string
  onDraftChange: (draft: string) => void
  // True while the change waits on the confirmation dialog, so the editor holds still behind it.
  isConfirming: boolean
  onStart: (institution: IBankInstitution) => void
  onCancel: () => void
  // Asks for confirmation; nothing is sent until the dialog is confirmed.
  onSave: (institution: IBankInstitution, displayOrder: number) => void
}

interface DisplayOrderCellProps {
  institution: IBankInstitution
  editing: DisplayOrderEditing
}

export const DisplayOrderCell: React.FC<DisplayOrderCellProps> = ({ institution, editing }) => {
  const current = institution.displayOrder
  const isEditing = editing.editingId === institution.id

  // 0 is a valid rank, so check for a number rather than falling back on a
  // truthiness test that would render it as N/A.
  const label = typeof current === 'number' ? String(current) : 'N/A'

  return (
    // The row opens the detail page on click; nothing done in here should.
    <Box
      onClick={event => event.stopPropagation()}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minHeight: 32 }}
    >
      {isEditing ? (
        <Editor institution={institution} editing={editing} />
      ) : (
        <>
          <StyledText>{label}</StyledText>
          {editing.canEdit && (
            <Tooltip title="Edit display order">
              {/* span so the tooltip still works on a disabled button */}
              <span>
                <IconButton
                  size="small"
                  aria-label={`Edit display order for ${institution.name}`}
                  disabled={editing.editingId !== null}
                  onClick={() => editing.onStart(institution)}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </>
      )}
    </Box>
  )
}

const Editor: React.FC<DisplayOrderCellProps> = ({ institution, editing }) => {
  const parsed = parseDisplayOrder(editing.draft)
  const invalid = parsed === null
  const helperText = invalid ? 'Enter a whole number, 0 or more' : undefined

  const save = () => {
    if (parsed === null || editing.isConfirming) return
    // Nothing to send when the value is what is already stored.
    if (parsed === institution.displayOrder) {
      editing.onCancel()
      return
    }
    editing.onSave(institution, parsed)
  }

  return (
    <>
      <TextField
        size="small"
        autoFocus
        value={editing.draft}
        onChange={event => editing.onDraftChange(event.target.value)}
        onKeyDown={event => {
          if (event.key === 'Enter') save()
          if (event.key === 'Escape' && !editing.isConfirming) editing.onCancel()
        }}
        disabled={editing.isConfirming}
        error={invalid}
        helperText={helperText}
        slotProps={{
          htmlInput: {
            inputMode: 'numeric',
            'aria-label': `Display order for ${institution.name}`,
          },
          formHelperText: { sx: { mx: 0, maxWidth: 200 } },
        }}
        sx={{ width: 90, '& input': { fontSize: '14px', py: 0.75 } }}
      />
      <IconButton
        size="small"
        aria-label="Save display order"
        disabled={invalid || editing.isConfirming}
        onClick={save}
        sx={{ color: '#067647' }}
      >
        <CheckIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Cancel editing"
        disabled={editing.isConfirming}
        onClick={editing.onCancel}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </>
  )
}

const StyledText = styled(Typography)({
  fontSize: '14px',
  fontFamily: 'Inter',
  color: '#475467',
  wordBreak: 'break-word',
})
